function updateMessage(el, vm) {
  vm.errors = Object.assign({}, vm.errors, {
    [el.name]: el.validationMessage,
  });
}

// Shared by Reports/Report.vue and Reports/Aggregated.vue: both poll
// POST /reporting/dataelement/:name and need to retry while the server is
// still building its result cache ('retry' in the response), memoized so
// concurrent requests for the same data element share one in-flight fetch.
export const ReportDataElementFetchMixin = {
  data: () => ({
    reportdata: {},
  }),
  methods: {
    fetchReportDataElement(cacheKey, elementName, requestData, onReady) {
      if (!(cacheKey in this.reportdata)) {
        this.reportdata[cacheKey] = this.$cfetch(`${this.$settings.APIBaseURL}/reporting/dataelement/${elementName}`, {
          cache: false,
          method: 'post',
          data: requestData,
        });
      }
      this.reportdata[cacheKey].then((json) => {
        if (json.status !== 'SUCCESS') return;
        if ('retry' in json) {
          window.setTimeout(() => {
            delete this.reportdata[cacheKey];
            this.fetchReportDataElement(cacheKey, elementName, requestData, onReady);
          }, 2000);
        } else {
          onReady(json.result);
        }
      });
    },
  },
};

export const ValidateMixin = {
  data: () => ({
    errors: {},
  }),
  computed: {
    hasErrors() {
      // Check if we have errors.
      return Object.keys(this.errors).some((key) => {
        return !!this.errors[key];
      });
    },
  },
  directives: {
    validate: {
      bind(el, _, vnode) {
        const vm = vnode.context;
        const tag = vnode.tag.toLowerCase();

        if(['input', 'textarea'].includes(tag)) {
          el.addEventListener('input', (e) => {
            updateMessage(e.target, vm);
          }); 
        } else if(tag == 'select') {
          el.addEventListener('change', (e) => {
            updateMessage(e.target, vm);
          });    
        } 
        vnode.context.$on('validate', () => {
          updateMessage(el, vm);
        });
      },
    },
  },
  methods: {
    validate() {
      this.$emit('validate');
    },
  },
};

// Built for CSV importers that are stepped, the usual strcture being:
// both drive a "pick CSV -> preview -> map columns -> review issues -> import"
// This mixin owns the parts that are usual to thssr processes;
//  each view keeps its own step-sequencing (numbering,
// order, and what happens after each call succeeds) since that differs.
export const CsvUploadWizardMixin = {
  data: () => ({
    file:           null,
    loadingPreview: false,
    preview:        null,

    validating:      false,
    validation:      null,
    skipInvalidRows: false,

    importing:     false,
    importResults: null,
  }),

  computed: {
    columnOptions() {
      if (!this.preview) return [];
      return [
        { value: null, text: '— not mapped —' },
        ...this.preview.headers.map((h, i) => ({ value: i, text: `col ${i}: ${h || '(empty)'}` })),
      ];
    },

    mappingValid() {
      return this.fieldDefs.filter(f => f.required).every(f => this.columnMap[f.key] !== null);
    },

    errorCount() {
      if (!this.validation) return 0;
      return this.validation.issues.filter(i => i.severity === 'error').length;
    },

    warningCount() {
      if (!this.validation) return 0;
      return this.validation.issues.filter(i => i.severity === 'warning').length;
    },

    skipLines() {
      if (!this.validation) return [];
      return this.validation.issues
        .filter(i => i.severity === 'error' || (i.severity === 'warning' && this.skipInvalidRows))
        .map(i => i.line);
    },
  },

  methods: {
    // Override in the consuming view to append flow-specific constant fields
    // (e.g. user_type) to every request.
    extraFormData() {
      return {};
    },

    buildFormData() {
      const fd = new FormData();
      fd.append('username',  this.$UserSession.getValue.call(this, 'username'));
      fd.append('authToken', this.$UserSession.getValue.call(this, 'authToken'));
      fd.append('user_id',   this.$UserSession.getValue('id'));
      Object.entries(this.extraFormData()).forEach(([key, value]) => fd.append(key, value));
      return fd;
    },

    autoDetectColumns(headers) {
      const lower = headers.map(h => (h || '').toLowerCase());
      this.fieldDefs.forEach(field => {
        const hints = field.hints || (() => {
          const bareKey = field.key.split('.').pop();
          return [bareKey.toLowerCase(), field.label.toLowerCase(), field.label.toLowerCase().replace(/\s+/g, '')];
        })();
        for (const hint of hints) {
          const idx = lower.findIndex(h => h.includes(hint));
          if (idx !== -1) { this.columnMap[field.key] = idx; return; }
        }
      });
    },

    fetchPreview(file) {
      this.file = file;
      this.preview = null;
      this.validation = null;
      this.skipInvalidRows = false;
      this.loadingPreview = true;

      const fd = this.buildFormData();
      fd.append('file', file);

      return fetch(`${this.$settings.APIBaseURL}${this.csvEndpoints.preview}`, { method: 'POST', body: fd })
        .then(r => r.json())
        .then(res => {
          if (res.status === 'SUCCESS') {
            this.preview = res;
            this.autoDetectColumns(res.headers);
          }
          return res;
        })
        .catch(err => { console.error('Preview error:', err); return null; })
        .finally(() => { this.loadingPreview = false; });
    },

    postValidation() {
      this.validating = true;
      this.validation = null;
      this.skipInvalidRows = false;

      const fd = this.buildFormData();
      fd.append('file', this.file);
      fd.append('column_map', JSON.stringify(this.columnMap));

      return fetch(`${this.$settings.APIBaseURL}${this.csvEndpoints.validate}`, { method: 'POST', body: fd })
        .then(r => r.json())
        .then(res => {
          if (res.status === 'SUCCESS') {
            this.validation = res;
          } else {
            console.error('Validation error:', res);
          }
          return res;
        })
        .catch(err => { console.error('Validation error:', err); return null; })
        .finally(() => { this.validating = false; });
    },

    postImport(extraFields = {}) {
      if (!this.file) return Promise.resolve(null);
      this.importing = true;

      const fd = this.buildFormData();
      fd.append('file', this.file);
      fd.append('column_map', JSON.stringify(this.columnMap));

      if (this.skipLines.length) {
        fd.append('skip_lines', JSON.stringify(this.skipLines));
      }

      Object.entries(extraFields).forEach(([key, value]) => fd.append(key, value));

      return fetch(`${this.$settings.APIBaseURL}${this.csvEndpoints.import}`, { method: 'POST', body: fd })
        .then(r => r.json())
        .then(res => {
          if (res.status === 'SUCCESS') {
            this.importResults = res;
          } else {
            console.error('Import failed:', res);
          }
          return res;
        })
        .catch(err => { console.error('Import error:', err); return null; })
        .finally(() => { this.importing = false; });
    },

    reset() {
      Object.assign(this.$data, this.$options.data.call(this));
    },
  },
};